import { signal, WritableSignal } from "@angular/core";
import { GridApi, GridOptions, GridReadyEvent } from "ag-grid-enterprise";
import { TableCellNameRendererComponent } from "@bitwarden/web-vault/app/components/tables/renderers/cell/account-name-renderer/table-cell-name-renderer.component";
import { ChevronCellRenderer } from "@bitwarden/web-vault/app/components/tables/renderers/cell/end-icon-renderer/end-icon-renderer.component";
import { TableParent } from "@bitwarden/web-vault/app/models/types/table.types";
import {
  InstitutionDetailsTableSubscription,
  TableFilter,
  TableSource,
} from "./institution-details-table.subscription";
import {
  getRowClass,
  getRowHeight,
} from "@bitwarden/web-vault/app/components/tables/tables.utils.js";

type InstitutionDetailsTableUIData = {
  name: string;
  icon: string;
};
type InstitutionDetailsTableSource = TableSource & InstitutionDetailsTableUIData;

export class InstitutionDetailsTable extends InstitutionDetailsTableSubscription {
  gridApi: GridApi;
  options: GridOptions<InstitutionDetailsTableSource>;
  institutionId: WritableSignal<string> = signal<string>(null);
  private selectedRowIndex: number;

  constructor(parent: TableParent) {
    super();

    this.options = {
      context: { parent },
      columnDefs: [
        {
          field: "name",
          cellRenderer: TableCellNameRendererComponent,
          flex: 2,
        },
        {
          field: "rightChevron",
          cellRenderer: ChevronCellRenderer,
          // Only the name cell takes focus.
          suppressNavigable: true,
          flex: 0.3,
          maxWidth: 50,
        },
      ],
      // The list is one Tab stop and the arrow keys move between rows (app-ag-grid-table's
      // tabToNextCell, #6246); Enter/Space run onRowClicked.
      suppressCellFocus: false,
      suppressContextMenu: true,
      onGridReady: (params: GridReadyEvent) => {
        this.gridApi = params.api;
        this.onRowClicked(this.institutionId());
      },
      onRowClicked: (params) => this.onRowClicked(params.node.data.id),
      getRowHeight: getRowHeight,
      getRowClass: (params) => getRowClass(params, this.selectedRowIndex),
    };
  }

  onRowClicked(institutionId: string): void {
    this.institutionId.set(institutionId);

    if (this.gridApi) {
      this.gridApi.forEachNode((node) => {
        if (node.data?.id === institutionId) {
          this.selectedRowIndex = node.rowIndex;
        }
      });
      // redrawRows() re-renders the focused cell too, dropping keyboard focus; put it back. Only
      // while a cell actually has focus: the grid remembers its last focused cell after focus
      // leaves, and setFocusedCell() forces browser focus, so a selection made from elsewhere
      // (the ?institutionId deep link) would yank focus back to that stale row. The focused DOM
      // cell must be this grid's remembered cell, not just any .ag-cell (another grid's).
      const activeCell = document.activeElement?.closest(".ag-cell");
      const lastFocusedCell = this.gridApi.getFocusedCell();
      const focusedCell =
        activeCell &&
        lastFocusedCell &&
        activeCell.getAttribute("col-id") === lastFocusedCell.column.getColId() &&
        activeCell.closest(".ag-row")?.getAttribute("row-index") ===
          String(lastFocusedCell.rowIndex)
          ? lastFocusedCell
          : null;
      this.gridApi.redrawRows();
      if (focusedCell) {
        this.gridApi.setFocusedCell(focusedCell.rowIndex, focusedCell.column);
      }
    }
  }
}
